import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Check, FolderKanban, GitBranch, Network, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { searchNodes } from '../../graph/graphQueries';
import { openGraphNode } from '../../graph/graphNavigation';
import { TYPE_STYLE } from '../graph/graphTheme';
import { TypeBadge, parentPath } from '../graph/GraphDetails';
import Highlight from '../graph/SearchHighlight';
import { useScopeLabel } from '../graph/GraphScopePicker';
import ProjectWorkspaceBinding from './ProjectWorkspaceBinding';
import ProjectRunSection from './run/ProjectRunSection';
import OpenInTerminalButton from './run/OpenInTerminalButton';
import { localPathOf } from '../../services/git/codeRepoClones';
import ProjectCodeSection from './ProjectCodeSection';

const RESULT_LIMIT = 8;

/**
 * An open project (owner, 13.09.2026: "search bigger and centered… similar to
 * how teams page look… similar to search engine where we can see in the
 * project the added files with nice UX").
 *
 * Layout: the Teams page column (max 900px, centred). Top to bottom:
 *   hero     colour mark · name · description · counts · Graph / Edit / Delete
 *   search   one large field that searches BOTH the project ("In this project",
 *            opens) and the workspace ("Add from workspace", adds) — results
 *            render in the page flow, never overflowing; ↑↓ + Enter, Esc, "/"
 *   filter   type chips (All · Collections 3 · Requests 5 …)
 *   items    grouped by type, rows with icon, name, where it lives, open, remove
 */
export function ProjectDetail({
  project,
  members,
  index,
  onAdd,
  onRemove,
  onOpenInGraph,
  onEdit,
  onDelete,
  onLinkRepo,
  repoInfo,
  // Team projects (27.09.2026): which workspace items may join (its own
  // team's only), and a viewer's read-only page — projectsStore refuses the
  // same writes; the UI just does not offer them.
  canAdd = () => true,
  readOnly = false,
  onError,
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [typeFilter, setTypeFilter] = useState(null);
  const [justAdded, setJustAdded] = useState(() => new Set());
  const inputRef = useRef(null);
  const scopeLabel = useScopeLabel();

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const query = q.trim();

  const { inProject, fromWorkspace } = useMemo(() => {
    if (!query) return { inProject: [], fromWorkspace: [] };
    const needle = query.toLowerCase();
    const inP = members
      .filter((m) => m.label.toLowerCase().includes(needle))
      .sort((a, b) => a.label.toLowerCase().indexOf(needle) - b.label.toLowerCase().indexOf(needle))
      .slice(0, RESULT_LIMIT);
    // Items added during this search stay listed (with a check) instead of
    // jumping away from under the cursor.
    const fromW = searchNodes(index, query, { limit: 60 })
      .filter((n) => n.type !== 'project' && canAdd(n) && (!memberIds.has(n.id) || justAdded.has(n.id)))
      .slice(0, RESULT_LIMIT);
    return { inProject: inP.filter((n) => !justAdded.has(n.id)), fromWorkspace: fromW };
  }, [query, members, index, memberIds, justAdded, canAdd]);

  // One flat list for keyboard navigation: project matches first, then adds.
  const flat = useMemo(
    () => [...inProject.map((n) => ({ kind: 'open', node: n })), ...fromWorkspace.map((n) => ({ kind: 'add', node: n }))],
    [inProject, fromWorkspace]
  );
  useEffect(() => {
    setActive(0);
    setJustAdded(new Set());
  }, [query]);

  // "/" focuses the search, like a search engine — unless the user is typing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const activate = (item) => {
    if (!item) return;
    if (item.kind === 'open') openGraphNode(item.node, index);
    else if (!justAdded.has(item.node.id)) {
      onAdd(item.node.id);
      setJustAdded((prev) => new Set(prev).add(item.node.id));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(flat[active]);
    } else if (e.key === 'Escape') {
      setQ('');
    }
  };

  const counts = useMemo(() => {
    const c = {};
    for (const m of members) c[m.type] = (c[m.type] || 0) + 1;
    return c;
  }, [members]);
  const typesPresent = Object.keys(TYPE_STYLE).filter((type) => counts[type]);
  const effectiveFilter = typeFilter && counts[typeFilter] ? typeFilter : null;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      if (effectiveFilter && m.type !== effectiveFilter) continue;
      if (!map.has(m.type)) map.set(m.type, []);
      map.get(m.type).push(m);
    }
    return Object.keys(TYPE_STYLE)
      .filter((type) => map.has(type))
      .map((type) => [type, map.get(type).sort((a, b) => a.label.localeCompare(b.label))]);
  }, [members, effectiveFilter]);

  // Linked repos with their records — a local `path` makes one runnable (Run section).
  const repos = useMemo(
    () => members.filter((m) => m.type === 'git_repo' && !m.needsFix).map((m) => repoInfo?.get(m.id)).filter(Boolean),
    [members, repoInfo]
  );

  const typeLabel = (type) => t(TYPE_STYLE[type]?.labelKey || 'graph.type.item');
  let flatIndex = -1;

  const resultRow = (node, kind) => {
    flatIndex += 1;
    const i = flatIndex;
    const added = kind === 'add' && justAdded.has(node.id);
    const path = parentPath(index, node.id);
    const where = scopeLabel(node);
    return (
      <li key={`${kind}:${node.id}`}>
        <button
          type="button"
          className={`pj-result${i === active ? ' pj-result--active' : ''}`}
          onMouseEnter={() => setActive(i)}
          onClick={() => activate({ kind, node })}
          aria-label={kind === 'add' ? t('projects.addNamed', { name: node.label }) : t('projects.openNamed', { name: node.label })}
        >
          <TypeBadge type={node.type} size="sm" />
          <span className="pj-result-text">
            <span className="pj-result-title">
              {node.method && <span className="pj-method">{node.method}</span>}
              <Highlight text={node.label} query={query} />
            </span>
            <span className="pj-result-meta">
              {typeLabel(node.type)}
              {where && ` · ${where}`}
              {path.length > 0 && ` · ${path.join(' › ')}`}
            </span>
          </span>
          {kind === 'open' ? (
            <span className="pj-result-action">
              <ArrowUpRight size={14} aria-hidden /> {t('graph.open')}
            </span>
          ) : added ? (
            <span className="pj-result-action pj-result-action--done">
              <Check size={14} aria-hidden /> {t('projects.added')}
            </span>
          ) : (
            <span className="pj-result-action pj-result-action--add">
              <Plus size={14} aria-hidden /> {t('projects.add')}
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <div className="pj-page" data-testid="project-detail">
      <header className="pj-hero">
        <span className="pj-mark-tile" style={{ background: project.color }} aria-hidden>
          <FolderKanban size={22} />
        </span>
        <div className="pj-hero-text">
          <h1 className="pj-title">{project.name}</h1>
          {(scopeLabel(project) || readOnly) && (
            <p className="pj-desc">{[scopeLabel(project), readOnly && t('projects.readOnly')].filter(Boolean).join(' · ')}</p>
          )}
          {project.description && <p className="pj-desc">{project.description}</p>}
          <div className="pj-stats">
            {members.length === 0 ? t('projects.noMembers') : t('projects.itemCount', { count: members.length })}
            {typesPresent.length > 0 && (
              <span className="pj-stats-types">
                {typesPresent.map((type) => (
                  <span key={type} className="pj-stat" title={typeLabel(type)}>
                    <TypeBadge type={type} size="xs" />
                    {counts[type]}
                  </span>
                ))}
              </span>
            )}
          </div>
          {/* Which API workspace the project opens (card 1.4). */}
          <ProjectWorkspaceBinding project={project} readOnly={readOnly} onError={onError} />
        </div>
        <div className="pj-hero-actions">
          <button type="button" className="gd-btn" onClick={onOpenInGraph}>
            <Network size={13} /> {t('projects.openInGraph')}
          </button>
          {!readOnly && onLinkRepo && (
            <button type="button" className="gd-btn" onClick={onLinkRepo} data-testid="link-repo">
              <GitBranch size={13} /> {t('projects.repo.linkButton')}
            </button>
          )}
          {!readOnly && (
            <>
              <button type="button" className="gd-btn gd-btn--icon" onClick={onEdit} title={t('projects.editTitle')} aria-label={t('projects.editTitle')}>
                <Pencil size={14} />
              </button>
              <button type="button" className="gd-btn gd-btn--icon pj-danger" onClick={onDelete} title={t('projects.deleteTitle')} aria-label={t('projects.deleteTitle')}>
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </header>

      <div className={`pj-search${query ? ' pj-search--open' : ''}`}>
        <div className="pj-search-field">
          <Search size={18} aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('projects.searchPlaceholder')}
            aria-label={t('projects.searchPlaceholder')}
            role="combobox"
            aria-expanded={!!query}
            aria-controls="pj-search-results"
            autoComplete="off"
          />
          {q ? (
            <button type="button" className="pj-search-clear" onClick={() => setQ('')} aria-label={t('common.close')}>
              <X size={14} />
            </button>
          ) : (
            <kbd className="pj-kbd" aria-hidden>
              /
            </kbd>
          )}
        </div>

        {query && (
          <div className="pj-results" id="pj-search-results">
            {flat.length === 0 && <div className="pj-results-empty">{t('projects.noResults')}</div>}
            {inProject.length > 0 && (
              <section>
                <div className="pj-results-head">{t('projects.inThisProject')}</div>
                <ul className="pj-results-list">{inProject.map((n) => resultRow(n, 'open'))}</ul>
              </section>
            )}
            {fromWorkspace.length > 0 && (
              <section>
                <div className="pj-results-head pj-results-head--row">
                  <span>{t('projects.addFromWorkspace')}</span>
                  {/* Bulk add (card 1.4): every listed item not added yet. */}
                  {fromWorkspace.some((n) => !justAdded.has(n.id)) && (
                    <button
                      type="button"
                      className="gd-btn"
                      data-testid="project-add-all"
                      onClick={() => fromWorkspace.forEach((n) => activate({ kind: 'add', node: n }))}
                    >
                      <Plus size={13} /> {t('projects.addAll')}
                    </button>
                  )}
                </div>
                <ul className="pj-results-list">{fromWorkspace.map((n) => resultRow(n, 'add'))}</ul>
              </section>
            )}
            {flat.length > 0 && <div className="pj-results-foot">{t('projects.searchKeys')}</div>}
          </div>
        )}
      </div>

      <ProjectRunSection repos={repos} projectName={project.name} />
      {/* Recent commits of the linked repositories, with their task keys (commit ↔ task links). */}
      <ProjectCodeSection repos={repos} members={members} readOnly={readOnly} />

      {members.length === 0 ? (
        <div className="pj-empty">
          <span className="pj-empty-icon" aria-hidden>
            <FolderKanban size={26} />
          </span>
          <h2>{t('projects.emptyTitle')}</h2>
          <p>{t('projects.noMembersHint')}</p>
          <button type="button" className="gd-btn gd-btn--primary pj-empty-cta" onClick={() => inputRef.current?.focus()}>
            <Search size={13} /> {t('projects.startSearching')}
          </button>
        </div>
      ) : (
        <>
          <div className="pj-filters" role="group" aria-label={t('projects.filterByType')}>
            <button type="button" className={`pj-filter${!effectiveFilter ? ' pj-filter--on' : ''}`} aria-pressed={!effectiveFilter} onClick={() => setTypeFilter(null)}>
              {t('graph.details.filterAll')} <span>{members.length}</span>
            </button>
            {typesPresent.map((type) => (
              <button
                key={type}
                type="button"
                className={`pj-filter${effectiveFilter === type ? ' pj-filter--on' : ''}`}
                aria-pressed={effectiveFilter === type}
                onClick={() => setTypeFilter(effectiveFilter === type ? null : type)}
              >
                {typeLabel(type)} <span>{counts[type]}</span>
              </button>
            ))}
          </div>

          {grouped.map(([type, items]) => (
            <section key={type} className="pj-group">
              <h3 className="pj-group-head">
                {typeLabel(type)}
                <span className="gd-count">{items.length}</span>
              </h3>
              <ul className="pj-list">
                {items.map((m) => {
                  const path = parentPath(index, m.id);
                  // A repository says where it is hosted (or that it is a local clone).
                  const repo = m.type === 'git_repo' ? repoInfo?.get(m.id) : null;
                  const place = m.type === 'git_repo' ? [m.host || t('projects.repo.localFolder'), repo?.branch].filter(Boolean).join(' · ') : path.join(' › ');
                  const where = [scopeLabel(m), place].filter(Boolean).join(' · ');
                  return (
                    <li key={m.id} className="pj-row" data-testid={m.needsFix ? 'pj-row-needs-fix' : undefined}>
                      <button
                        type="button"
                        className="pj-row-main"
                        // A link that needs fixing has no graph node to open (05.10.2026).
                        onClick={m.needsFix ? undefined : () => openGraphNode(m, index)}
                        disabled={m.needsFix}
                        title={m.needsFix ? undefined : t('graph.open')}
                      >
                        <TypeBadge type={m.type} size="sm" />
                        <span className="pj-result-text">
                          <span className="pj-result-title">
                            {m.method && <span className="pj-method">{m.method}</span>}
                            {m.label}
                          </span>
                          {where && <span className="pj-result-meta">{where}</span>}
                          {repo?.description && <span className="pj-result-meta">{repo.description}</span>}
                          {m.needsFix && <span className="pj-result-meta pj-needs-fix">{t('projects.repo.needsFix')}</span>}
                        </span>
                        {!m.needsFix && <ArrowUpRight size={14} className="pj-row-open" aria-hidden />}
                      </button>
                      {/* "Open in terminal": the repo's local clone on this device (disabled without one). */}
                      {m.type === 'git_repo' && !m.needsFix && (
                        <OpenInTerminalButton
                          repoPath={repo ? repo.path || localPathOf(repo) || null : null}
                          projectName={project.name}
                          className="pj-row-terminal"
                          testId="repo-row-terminal"
                        />
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          className="graph-icon-btn pj-row-remove"
                          onClick={() => onRemove(m.id)}
                          title={t('projects.removeMember')}
                          aria-label={t('projects.removeMember')}
                        >
                          <X size={13} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
